import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Send, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { speak, stopSpeaking, getRecognizer, findVoice, VOICE_LANGS } from "@/lib/speech";
import { feeLabel, slaLabel } from "@/components/ServiceRow";

const Wave = () => <span className="q1-wave inline-flex h-6 items-center" aria-hidden="true"><span /><span /><span /><span /><span /></span>;

export const VoiceAssistant = () => {
  const { t, lang, setLang, voiceLang, setVoiceLang, consent, assistantOpen, setAssistantOpen, svcName, catalog, refreshMe } = useApp();
  const nav = useNavigate();
  const [phase, setPhase] = useState("idle");
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState([]);
  const recRef = useRef(null);
  const sessionId = useRef(`vs-${Date.now()}`);
  const asrSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const quality = VOICE_LANGS.find((l) => l.code === voiceLang)?.quality;

  useEffect(() => { if (!assistantOpen) { stopSpeaking(); recRef.current?.abort?.(); setPhase("idle"); } }, [assistantOpen]);

  const startRequest = async (service) => {
    try {
      const { data } = await api.post("/requests", { service_id: service.service_id, input_mode: "voice" });
      sessionStorage.setItem(`q1_mfa_${data.ref}`, data.sandbox_mfa_code);
      refreshMe();
      setAssistantOpen(false);
      nav(`/requests/${data.ref}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ask = async (text, mode) => {
    if (!text.trim()) return;
    setTurns((x) => [...x, { who: "me", text }]);
    setInput("");
    setPhase("thinking");
    try {
      const { data } = await api.post("/assistant", { text, lang: voiceLang, input_mode: mode, consent, session_id: sessionId.current, screen: "assistant" });
      setTurns((x) => [...x, { who: "bot", text: data.reply, service: data.service, intent: data.intent }]);
      const replyLang = VOICE_LANGS.some((l) => l.code === data.detected_lang) ? data.detected_lang : voiceLang;
      if (!findVoice(replyLang)) toast.warning(t("noVoice"));
      speak(data.reply, replyLang, 1, { onstart: () => setPhase("speaking"), onend: () => setPhase("idle") });
      setPhase("speaking");
      if (data.intent === "switch_lang" && data.switch_lang) setLang(data.switch_lang);
      if (data.intent === "sector_browse" && data.sector_id) { setAssistantOpen(false); nav(`/services?sector=${data.sector_id}`); }
      if (data.intent === "request_status") { setAssistantOpen(false); nav("/requests"); }
      if (data.intent === "start_request" && data.service) startRequest(data.service);
    } catch (e) {
      setTurns((x) => [...x, { who: "bot", text: errMsg(e) }]);
      setPhase("idle");
    }
  };

  const listen = () => {
    if (phase === "listening") { recRef.current?.stop(); return; }
    stopSpeaking();
    const r = getRecognizer(voiceLang);
    if (!r) { toast.info(t("unsupportedAsr")); return; }
    recRef.current = r;
    let finalText = "";
    r.onresult = (ev) => {
      finalText = Array.from(ev.results).map((x) => x[0].transcript).join(" ");
      setInput(finalText);
    };
    r.onerror = () => setPhase("idle");
    r.onend = () => { if (finalText) ask(finalText, "voice"); else setPhase("idle"); };
    setPhase("listening");
    r.start();
  };

  const intents = catalog?.voice_intents || [];
  const phaseText = { idle: t("idle"), listening: t("listening"), thinking: t("thinking"), speaking: t("speaking") }[phase];

  return (
    <Sheet open={assistantOpen} onOpenChange={setAssistantOpen}>
      <SheetContent side="bottom" className="mx-auto flex h-[85vh] max-w-md flex-col rounded-t-3xl p-0" data-testid="voice-assistant-sheet">
        <SheetHeader className="border-b px-5 pb-3 pt-5 text-start">
          <SheetTitle className="flex items-center gap-2"><Sparkles size={18} className="text-[#B8901F]" /> {t("assistant")}</SheetTitle>
          <SheetDescription className="text-xs">{t("quality")}: {quality} · {t("voiceLanguage")}:
            <select data-testid="assistant-voice-lang-select" value={voiceLang} onChange={(e) => setVoiceLang(e.target.value)} className="ms-1 rounded border bg-card px-1 py-0.5 text-xs">
              {VOICE_LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </SheetDescription>
        </SheetHeader>
        <div className="q1-scroll flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="assistant-transcript" aria-live="polite">
          {turns.length === 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("examples")}</p>
              <div className="flex flex-wrap gap-2">
                {intents.slice(0, 8).map((i) => (
                  <button key={i.intent} data-testid={`assistant-example-${i.intent}`} onClick={() => ask(lang === "ar" ? i.example_ar : i.example_en, "typed")}
                    className="rounded-full border border-border bg-muted px-3 py-1.5 text-xs transition-colors hover:border-primary hover:text-primary">
                    {lang === "ar" ? i.example_ar : i.example_en}
                  </button>
                ))}
              </div>
            </div>
          )}
          {turns.map((m, i) => (
            <div key={i} className={`q1-fade-up flex ${m.who === "me" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.who === "me" ? "bg-primary text-primary-foreground" : "border bg-card"}`} data-testid={m.who === "bot" ? "assistant-reply" : "assistant-user-msg"}>
                {m.text}
                {m.service && (
                  <div className="mt-2 rounded-xl border bg-muted/60 p-2.5 text-xs text-foreground">
                    <p className="font-semibold">{svcName(m.service)}</p>
                    <p className="text-muted-foreground">{feeLabel(m.service.fee_qar, t)} · {slaLabel(m.service.sla_days, t)}</p>
                    <div className="mt-2 flex gap-2">
                      <button data-testid="assistant-open-service-btn" onClick={() => { setAssistantOpen(false); nav(`/services/${m.service.service_id}`); }} className="rounded-full border px-3 py-1.5 font-semibold">{t("openService")}</button>
                      <button data-testid="assistant-start-request-btn" onClick={() => startRequest(m.service)} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 font-semibold text-primary-foreground">{t("startRequest")} <ArrowRight size={12} className="q1-flip" /></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t px-5 pb-6 pt-3">
          <div className="mb-3 flex items-center justify-center gap-2 text-xs font-semibold text-primary" data-testid="assistant-phase">
            {phase === "thinking" ? <Loader2 size={16} className="animate-spin" /> : phase !== "idle" ? <Wave /> : null}
            {phaseText}
          </div>
          {!asrSupported && <p className="mb-2 text-center text-[11px] text-muted-foreground">{t("unsupportedAsr")}</p>}
          <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); ask(input, "typed"); }}>
            <button type="button" data-testid="assistant-mic-btn" onClick={listen} aria-label={phase === "listening" ? t("stop") : t("listening")}
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105 ${phase === "listening" ? "bg-[#B8901F] animate-pulse" : "bg-primary"}`}>
              {asrSupported ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            <input data-testid="assistant-text-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("typeQuestion")}
              className="h-12 flex-1 rounded-full border border-input bg-card px-4 text-sm" />
            <button type="submit" data-testid="assistant-send-btn" aria-label={t("send")} className="flex h-12 w-12 items-center justify-center rounded-full border bg-card">
              <Send size={18} className="q1-flip" />
            </button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
